import { useEffect, useState } from 'react'
import { useCommonT } from '../../i18n'
import { registerTransitionConfirmer } from '../../lib/offline/transitionConfirm'
import { C3_REASON, type C3Reason } from '../../lib/offline/offline.codes'
import ModalShell from '../shared/ModalShell'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'

interface Ask {
  count: number
  reason: C3Reason
  resolve: (go: boolean) => void
}

/**
 * C3 step 4 for transitions no screen asked about itself (an account switch
 * from the sign-in modal or an OAuth return): "N changes are not saved yet
 * and will be removed". Mounted once by the app shell. Sign-out passes its
 * own confirmation (AuthButton).
 */
export default function TransitionConfirmHost() {
  const t = useCommonT()
  const [ask, setAsk] = useState<Ask | null>(null)
  useEffect(() => registerTransitionConfirmer((count, reason) => new Promise<boolean>(resolve => {
    setAsk({ count, reason, resolve })
  })), [])
  if (!ask) return null
  const answer = (go: boolean) => {
    ask.resolve(go)
    setAsk(null)
  }
  const body = ask.reason === C3_REASON.accountSwitch
    ? t('offline.switch_warning', { count: ask.count })
    : t('offline.signout_warning', { count: ask.count })
  return (
    <ModalShell
      onClose={() => answer(false)}
      label={body}
      className={`fixed inset-0 z-[96] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 outline-none ${OVERLAY_ABOVE_AD}`}
    >
      <div data-testid="transition-confirm" className={`surface p-6 max-w-md w-full space-y-4 ${PANEL_FITS_OVERLAY}`}>
        <p className="text-sm text-gray-200">{body}</p>
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => answer(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200">
            {t('offline.confirm_cancel')}
          </button>
          <button type="button" onClick={() => answer(true)} className="px-4 py-2 bg-red-900/60 text-red-200 text-sm font-medium rounded-lg hover:bg-red-900 transition-colors">
            {t('offline.confirm_continue')}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}
